import { IconButton } from "@mui/material";
import { Stack } from "@mui/system";
import CheckIcon from "@mui/icons-material/Check";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import {
  colors,
  spacing,
  typography,
} from "../../../../../../../../../assets/styles/tokens";
import { useEffect, useState } from "react";
import { buiDashboardEndPointLogicMobile } from "../../../../../../../../../logic/AssetManagement/overview/BusinessLeaderDashboardMobile/buiDashboardEndPointLogicMobile";
import { useDispatch, useSelector } from "react-redux";
import { selectUserToken } from "../../../../../../../../../features/userSlice";
import { useSearchParams } from "react-router-dom";
import {
  last12MonthsFullQueryIdWebsite,
  last12MonthsProportionalQueryIdWebsite,
  last30DaysQueryIdWebsite,
  last6WeeksQueryIdWebsite,
} from "../../../../helpers/QIDs";
import { setLoadingWebsiteData } from "../../../../../../../../../features/businessLeaderDashboardSlice";
import { saveAs } from "file-saver";
import dayjs from "dayjs";
import { sitePerformanceAll } from "../../../../../AdditionalQueries/AdditionalQueriesAccordion/AdditionalQueriesConstants";

const BLDWebsiteDateFilter = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const periodFromUrl = searchParams.get("periodFromWebsite");
  const { loadingWebsiteData, websiteData } = useSelector(
    (state: any) => state.businessLeaderDashBoard,
  );
  const tableHeadingsAll = sitePerformanceAll;
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const dispatch = useDispatch();

  const websiteSharedUids = searchParams.get("workspace");
  const queryConfigMap: Record<
    string,
    { queryId: string; period_type?: "Full" | "Proportional" }
  > = {
    "Last 30 Days": {
      queryId: last30DaysQueryIdWebsite,
    },
    "Last 6 Weeks": {
      queryId: last6WeeksQueryIdWebsite,
    },
    "Last 12 Months Full": {
      queryId: last12MonthsFullQueryIdWebsite,
      period_type: "Full",
    },
    "Last 12 Month Proportional": {
      queryId: last12MonthsProportionalQueryIdWebsite,
      period_type: "Proportional",
    },
  };

  const handleChange = (_: any, newValue: string | null) => {
    if (!newValue) return;
    setValue(newValue);
    let paramValue;
    if (newValue === "Last 30 Days") {
      paramValue = "last30";
    } else {
      paramValue = periodParamMap[newValue];
    }
    const newParams = new URLSearchParams(searchParams);
    newParams.set("periodFromWebsite", paramValue);
    setSearchParams(newParams);
  };

  const getLast30DaysLabel = () => {
    const today = new Date();
    const endDate = new Date(today);
    endDate.setDate(today.getDate() - 1);
    const startDate = new Date(today);
    startDate.setDate(today.getDate() - 31);
    const format = (date: Date) => date.toLocaleDateString("en-GB"); // dd/mm/yyyy
    return `Last 30 Days (${format(startDate)} - ${format(endDate)})`;
  };

  const getLast6WeeksLabel = () => {
    const today = new Date();
    const endDate = new Date(today);
    endDate.setDate(today.getDate() - 1);
    const startDate = new Date(today);
    startDate.setDate(today.getDate() - 42);
    const format = (date: Date) => date.toLocaleDateString("en-GB");
    return `Last 6 Weeks (${format(startDate)} - ${format(endDate)})`;
  };
  const options = [
    "Last 30 Days",
    "Last 6 Weeks",
    "Last 12 Months Full",
    "Last 12 Month Proportional",
  ];
  const periodParamMap: Record<string, string> = {
    "Last 6 Weeks": "last6weeks",
    "Last 12 Months Full": "last12full",
    "Last 12 Month Proportional": "last12prop",
  };

  const reversePeriodMap: Record<string, string> = {
    last30: "Last 30 Days",
    last6weeks: "Last 6 Weeks",
    last12full: "Last 12 Months Full",
    last12prop: "Last 12 Month Proportional",
  };
  const [value, setValue] = useState<string | null>(() => {
    if (!periodFromUrl) return "Last 12 Month Proportional";
    return reversePeriodMap[periodFromUrl];
  });
  const getDisplayLabel = (option: string) => {
    if (option === "Last 30 Days") {
      return getLast30DaysLabel();
    }
    if (option === "Last 6 Weeks") {
      return getLast6WeeksLabel();
    }
    return option;
  };
  useEffect(() => {
    if (!value) return;
    const configKey = value;
    const { queryId, period_type } = queryConfigMap[configKey];
    const fetchData = async () => {
      try {
        dispatch(setLoadingWebsiteData(true));
        const dimensionResponse =
          await buiDashboardEndPointLogicMobile.getDimensionInBizLeaderDashboardMobile(
            oauthTokenHeader,
          );
        if (!dimensionResponse || dimensionResponse.status !== 200) return;
        const dimensions = dimensionResponse?.data?.data || [];
        const matchedDimensions = dimensions
          ?.filter((dim: any) =>
            dim.websites?.some(
              (site: any) => site.shared_uid === websiteSharedUids,
            ),
          )
          ?.map((dim: any) => dim.dimension);
        const dimensionsToUse =
          matchedDimensions?.length > 0
            ? matchedDimensions
            : dimensions?.length > 0
              ? [dimensions[0].dimension]
              : [];

        if (!dimensionsToUse.length) return;
        await buiDashboardEndPointLogicMobile.getWebsiteDataInBizLeaderDashboardMobile(
          {
            oauthTokenHeader,
            queryId: queryId,
            websiteSharedUids: websiteSharedUids?.split(",") || [],
            type: "site",
            dimension: dimensionsToUse,
            period_type,
          },
        );
      } finally {
        dispatch(setLoadingWebsiteData(false));
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, websiteSharedUids]);

  const getRowCSV = (col: any, row: any) => {
    if (row[col?.key] === null) {
      return "null";
    } else if (col?.key === "date") {
      if (row?.date) {
        return `${row?.day} ${dayjs(row?.date).format("DD-MM-YYYY")}`;
      } else if (row?.month) {
        return row?.month;
      } else if (row?.start_of_week) {
        return row?.start_of_week;
      }
    } else if (
      col?.label === "CVR%" ||
      col?.label === "% RS From Total Income" ||
      col?.key === "roi_perc" ||
      col?.key === "ng_ftd_br_ftd"
    ) {
      let value = row[col?.key] * 100;
      value = Math.round(value);
      return value + "%";
    } else if (
      row[col?.key] !== 0 &&
      col?.key !== "date" &&
      col?.key !== "br_ftd" &&
      col?.key !== "ng_ftd" &&
      col?.key !== "qftds" &&
      col?.key !== "qftds" &&
      col?.key !== "click_in" &&
      col?.key !== "click_out"
    ) {
      let value = Math.round(row[col?.key]);
      return value + "$";
    } else {
      return row[col?.key];
    }
  };

  const handleExportCSV = () => {
    const csvRows = [];
    const headers = tableHeadingsAll.map((pair) => pair.key) as any;
    csvRows.push(headers.join(","));
    // eslint-disable-next-line
    websiteData?.query_output?.map((row: any) => {
      let rowData = [] as any;
      // eslint-disable-next-line
      tableHeadingsAll.map((key: any) => {
        const result = getRowCSV(key, row);
        if (result !== "null") {
          rowData.push(result);
        }
      });
      if (rowData?.length > 0) {
        csvRows.push(rowData.join(","));
      }
    });
    const csvString = csvRows.join("\n");
    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "SitePerformance.csv");
  };

  return (
    <>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          mt: spacing.Spacing.xs.$value,
        }}
      >
        <Autocomplete
          options={options}
          value={value}
          getOptionLabel={(option) => getDisplayLabel(option)}
          data-tracker="671"
          onChange={handleChange}
          disabled={loadingWebsiteData}
          sx={{
            width: "80%",
            "& .MuiInputBase-input": {
              fontSize: typography.Size.Text.sm.$value,
              lineHeight: typography.Line_height.text.sm.$value,
              fontWeight: typography.Font_weight.Semibold.$value,
              py: "0px !important",
            },
            "& .MuiOutlinedInput-root": {
              borderRadius: spacing.Radius.md.$value,
              "&.Mui-focused fieldset": {
                borderColor: colors.GrayLightMode[300].$value,
                borderWidth: "1px",
              },
            },
          }}
          size="small"
          renderOption={(props, option, { selected }) => (
            <li
              {...props}
              style={{
                display: "flex",
                alignItems: "center",
                paddingLeft: spacing.Spacing["5xl"].$value,
                position: "relative",
                backgroundColor: selected ? colors.Brand[50].$value : "",
              }}
            >
              {selected && (
                <CheckIcon
                  fontSize="small"
                  sx={{
                    position: "absolute",
                    left: spacing.Spacing.lg.$value,
                  }}
                />
              )}
              <span style={{}}>{option}</span>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              color="secondary"
              {...params}
              value={value ? getDisplayLabel(value) : ""}
            />
          )}
        />

        <IconButton
          color="secondary"
          size="large"
          disabled={loadingWebsiteData}
          onClick={handleExportCSV}
          data-tracker="672"
        >
          <FileDownloadIcon sx={{ color: colors.GrayLightMode[700].$value }} />
        </IconButton>
      </Stack>
    </>
  );
};

export default BLDWebsiteDateFilter;
